import { convertQwertyToHangul, disassemble, getChoseong } from "es-hangul";

const COMPATIBILITY_CONSONANTS = new Set([
	"ㄱ",
	"ㄲ",
	"ㄳ",
	"ㄴ",
	"ㄵ",
	"ㄶ",
	"ㄷ",
	"ㄸ",
	"ㄹ",
	"ㄺ",
	"ㄻ",
	"ㄼ",
	"ㄽ",
	"ㄾ",
	"ㄿ",
	"ㅀ",
	"ㅁ",
	"ㅂ",
	"ㅃ",
	"ㅄ",
	"ㅅ",
	"ㅆ",
	"ㅇ",
	"ㅈ",
	"ㅉ",
	"ㅊ",
	"ㅋ",
	"ㅌ",
	"ㅍ",
	"ㅎ",
]);

function normalizeSearchText(value: string) {
	return value.toLowerCase().replace(/\s+/g, "");
}

function isConsonantOnlyQuery(value: string) {
	return (
		value.length > 0 &&
		[...value].every((char) => COMPATIBILITY_CONSONANTS.has(char))
	);
}

// Reverse a mistyped English-layout query (e.g. "thffksk") into the Hangul the
// user intended ("솔라나"). Returns null when the query is clearly not a typo —
// the strongest gate is that the converted result must contain at least one
// completed Hangul syllable, which filters out genuine English like "bit".
function tryQwertyToHangul(query: string): string | null {
	if (!/^[a-z]{2,}$/.test(query)) return null;
	let converted: string;
	try {
		// es-hangul's `assemble` throws on QWERTY sequences whose Hangul
		// mapping starts with a vowel (e.g. "hkd" → [ㅗ, ㅏ, ㅇ] — ㅗ can't
		// be a choseong). Treat those as non-typos and fall through.
		converted = convertQwertyToHangul(query);
	} catch {
		return null;
	}
	const hasSyllable = [...converted].some(
		(char) => char >= "가" && char <= "힣",
	);
	return hasSyllable ? converted : null;
}

export function matchesSearchQuery(query: string, candidates: string[]) {
	const normalizedQuery = normalizeSearchText(query);
	if (!normalizedQuery) return true;

	const disassembledQuery = disassemble(normalizedQuery);
	const consonantOnlyQuery = isConsonantOnlyQuery(normalizedQuery);
	const convertedQuery = tryQwertyToHangul(normalizedQuery);
	const disassembledConverted = convertedQuery
		? disassemble(convertedQuery)
		: null;

	return candidates.some((candidate) => {
		const normalizedCandidate = normalizeSearchText(candidate);
		if (!normalizedCandidate) return false;
		if (normalizedCandidate.includes(normalizedQuery)) return true;

		const disassembledCandidate = disassemble(normalizedCandidate);
		if (disassembledCandidate.includes(disassembledQuery)) return true;
		if (
			disassembledConverted &&
			disassembledCandidate.includes(disassembledConverted)
		)
			return true;

		if (!consonantOnlyQuery) return false;

		return getChoseong(normalizedCandidate).includes(normalizedQuery);
	});
}
